"use client";

import { ArrowDown, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TechHospitalityHero() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const capabilities = [{
    number: "[01]",
    title: "OTA REVENUE MANAGEMENT",
    metric: "+22% REVPAR",
    description: "Data-driven channel optimization strategies."
  }, {
    number: "[02]",
    title: "HOSPITALITY TECH STACK",
    metric: "SYSTEM SYNC",
    description: "Seamless integration of PMS and CRS tools."
  }, {
    number: "[03]",
    title: "REVENUE AUDITS",
    metric: "GAP ANALYSIS",
    description: "Identifying hidden profit opportunities daily."
  }];
  const tickerItems = ["OTA STRATEGY", "REVENUE GROWTH", "PMS INTEGRATION", "MARKET ANALYSIS", "PROFIT MAXIMIZATION", "DEMAND FORECASTING", "TECH IMPLEMENTATION", "AUDIT INSIGHTS", "REVENUE OPTIMIZATION"];
  return <section data-section-id="4314" id="techhospitalityhero" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0 z-0">
        <video className="h-full w-full object-cover opacity-60" autoPlay muted loop playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.webp">
          <source src="https://cdn.coverr.co/videos/coverr-modern-hotel-lobby-1572/1080p.mp4" type="video/mp4" />
        </video>
        {}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to bottom, hsla(222, 47%, 11%, 0.8) 0%, hsla(222, 47%, 11%, 0.5) 50%, hsla(222, 47%, 11%, 0.9) 100%)"
      }} />
      </div>

      {}
      <div className="absolute left-4 top-6 z-20 flex items-center gap-3 md:left-8 md:top-8">
        <span className="font-mono text-[10px] tracking-widest text-foreground/70 md:text-xs">
          // CONSULTANCY EST. 2024
        </span>
        <span className="hidden h-px w-16 bg-foreground/30 sm:block lg:w-24" />
        <span className="hidden font-mono text-[10px] tracking-widest text-primary sm:inline md:text-xs">
          SUNIL SINGH
        </span>
      </div>

      {}
      <div className="absolute right-4 top-6 z-20 hidden font-mono text-[10px] tracking-widest text-foreground/60 md:right-8 md:top-8 md:block md:text-xs">
        HOTELREV / CONSULT
      </div>

      {}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col px-4 pt-24 pb-32 md:px-8 lg:flex-row lg:items-center lg:pt-32">
        {}
        <div ref={headlineRef} className={`flex-1 transition-all duration-1000 ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h1 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-foreground">
            <span className="block text-5xl sm:text-7xl md:text-8xl lg:text-[120px] xl:text-[160px]">
              MAXIMIZE
            </span>
            <span className="block font-serif italic text-5xl sm:text-7xl md:text-8xl lg:text-[120px] xl:text-[160px]" style={{
            fontWeight: 400
          }}>
              Hotel
            </span>
            <span className="block text-5xl sm:text-7xl md:text-8xl lg:text-[120px] xl:text-[160px]">
              REVENUE
            </span>
          </h1>

          <p className="mt-8 max-w-[480px] font-serif text-base leading-relaxed text-foreground/80 md:text-lg">
            HotelRevBaBa provides expert revenue management consulting. We help hotel owners and investors leverage data-driven OTA strategies to increase profitability.
          </p>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link to="/ContactPage">
              <Button size="lg" className="group h-14 bg-primary px-8 font-default text-sm font-bold uppercase tracking-widest text-primary-foreground shadow-2xl hover:bg-primary/90">
                BOOK CONSULTATION
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/ServicesPage">
              <Button variant="outline" size="lg" className="h-14 border-foreground/30 bg-transparent px-8 font-default text-sm font-bold uppercase tracking-widest text-foreground hover:bg-foreground/5 hover:text-foreground">
                VIEW SERVICES
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={`mt-16 flex flex-col gap-5 lg:ml-12 lg:mt-0 lg:w-[340px] transition-all duration-1000 delay-200 ${cardsVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"}`}>
          {capabilities.map((cap, i) => <div key={i} data-index={i} className="group relative border border-foreground/10 p-6 shadow-2xl backdrop-blur-xl transition-all duration-300 hover:border-primary hover:shadow-primary/20" style={{
          backgroundColor: "hsla(222, 47%, 11%, 0.4)",
          marginLeft: i === 1 ? "1.5rem" : i === 2 ? "0.75rem" : "0"
        }}>
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs tracking-widest text-foreground/50">
                  {cap.number}
                </span>
                <span className="font-mono text-xs tracking-widest text-primary">
                  {cap.metric}
                </span>
              </div>
              <h3 className="mt-6 font-default text-xl font-bold uppercase leading-tight tracking-tight text-foreground">
                {cap.title}
              </h3>
              <p className="mt-3 font-serif text-sm leading-relaxed text-foreground/70">
                {cap.description}
              </p>
              <div className="mt-5 h-px w-full bg-foreground/10 transition-all duration-300 group-hover:bg-primary" />
            </div>)}
        </div>
      </div>

      {}
      <div className="absolute bottom-12 left-0 right-0 z-10 overflow-hidden border-y border-foreground/10 bg-background/40 py-3 backdrop-blur-sm">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span key={i} data-index={i} className="mx-8 inline-flex items-center font-mono text-xs tracking-widest text-foreground/60">
              <span className="mr-8 text-primary">·</span>
              {item}
            </span>)}
        </div>
      </div>

      {}
      <div className="absolute bottom-4 right-4 z-20 flex flex-col items-center gap-2 md:right-8">
        <span className="font-mono text-[10px] tracking-widest text-foreground/60">
          SCROLL
        </span>
        <div className="flex h-12 w-px items-end justify-center bg-foreground/30">
          <ArrowDown className="size-3 animate-bounce text-foreground/60" />
        </div>
      </div>

      {}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
      `}</style>
    </section>;
}